'use client';

/**
 * ASK LAB — the laboratory's command interface. A palette, not a chatbot:
 * quick commands, free-text questions streamed from the server-side model
 * gateway, and a registry terminal. Mounted globally in the root layout; also hosts
 * the global AI_ACTION_REQUESTED bus subscription and the CompareView.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useRouter } from 'next/navigation';
import { useWorld } from '../lab/store';
import { lockScroll, unlockScroll } from '../lab/scrollLock';
import { onLabEvent } from '../lab/events';
import type { UIAction } from './actions';
import { describeAction, executeAction } from './dispatch';
import { useAsk } from './useAsk';
import { runTerminalCommand, TERMINAL_BANNER, type TermLine } from './terminal';
import CompareView from './CompareView';
import { useLang } from '../i18n/client';
import { getDict } from '../i18n/dictionary';
import { localePath, type Lang } from '../i18n/config';
import styles from './AskLab.module.css';

type Mode = 'palette' | 'terminal';

/**
 * Accessibility labels not part of the shared UI-chrome dictionary. Kept local
 * (a per-component bilingual copy object) so the read-only dictionary stays
 * untouched; the human-voice register is fully translated.
 */
const copy: Record<Lang, {
  dialogAria: string;
  closeAria: string;
  quickAria: string;
  termInputAria: string;
  askInputAria: string;
  stopAria: string;
  termPlaceholder: string;
}> = {
  en: {
    dialogAria: 'Ask Lab — laboratory interface',
    closeAria: 'Close Ask Lab',
    quickAria: 'Quick commands',
    termInputAria: 'Terminal command',
    askInputAria: 'Ask the laboratory or pick a command',
    stopAria: 'Stop the current answer',
    // All tokens are English command keywords (the machine contract) — same
    // in both locales.
    termPlaceholder: 'help · list research · system status · exit',
  },
  ru: {
    dialogAria: 'Ask Lab — интерфейс лаборатории',
    closeAria: 'Закрыть Ask Lab',
    quickAria: 'Быстрые команды',
    termInputAria: 'Команда терминала',
    askInputAria: 'Спросите лабораторию или выберите команду',
    stopAria: 'Остановить текущий ответ',
    termPlaceholder: 'help · list research · system status · exit',
  },
};

interface QuickCommand {
  id: string;
  label: string;
  hint: string;
  perform: () => void;
}

function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
  );
}

const TONE_CLASS: Record<NonNullable<TermLine['tone']>, string> = {
  default: '',
  accent: 'termAccent',
  dim: 'termDim',
  error: 'termError',
};

export default function AskLab() {
  const open = useWorld((s) => s.paletteOpen);
  const setOpen = useWorld((s) => s.setPaletteOpen);
  const reducedMotion = useWorld((s) => s.reducedMotion);
  const router = useRouter();
  const lang = useLang();
  const dict = getDict(lang);
  const c = copy[lang];

  const [mode, setMode] = useState<Mode>('palette');
  const [input, setInput] = useState('');
  const [sel, setSel] = useState(-1);
  const [termLines, setTermLines] = useState<TermLine[]>(() => TERMINAL_BANNER(lang));

  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const lastActiveRef = useRef<HTMLElement | null>(null);

  // ── AI action execution (500ms beat, staggered) ────────────────────────
  const runActions = useCallback(
    (actions: UIAction[]) => {
      actions.forEach((a, i) => {
        window.setTimeout(() => {
          executeAction(a, router, lang);
          // The comparison overlay is the payoff — reveal it.
          if (a.type === 'COMPARE') useWorld.getState().setPaletteOpen(false);
        }, 500 + i * 400);
      });
    },
    [router, lang]
  );

  const { exchanges, busy, ask, cancel } = useAsk(runActions);

  // ── Global bus: the 3D world routes its clicks through here ───────────
  useEffect(
    () =>
      onLabEvent((e) => {
        if (e.type === 'AI_ACTION_REQUESTED') executeAction(e.action, router, lang);
      }),
    [router, lang]
  );

  // ── Global keyboard: ⌘K / Ctrl+K toggle, '/' opens, Escape closes ─────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(!useWorld.getState().paletteOpen);
      } else if (
        e.key === '/' &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !useWorld.getState().paletteOpen &&
        !isEditableTarget(e.target)
      ) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && useWorld.getState().paletteOpen) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  // ── Open/close: focus capture + body scroll lock + focus return ───────
  useEffect(() => {
    if (!open) return;
    lastActiveRef.current = document.activeElement as HTMLElement | null;
    lockScroll();
    const t = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => {
      window.clearTimeout(t);
      unlockScroll();
      lastActiveRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [mode, open]);

  useEffect(() => setSel(-1), [input, mode]);

  // Auto-scroll transcript/terminal to the latest line.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [exchanges, termLines, mode, open]);

  const close = useCallback(() => setOpen(false), [setOpen]);

  // ── Ask flow ──────────────────────────────────────────────────────────
  const submitAsk = useCallback(
    (q: string) => {
      if (busy) return;
      useWorld.getState().recordAsk();
      void ask(q);
    },
    [ask, busy]
  );

  // ── Terminal flow ─────────────────────────────────────────────────────
  const runTerm = useCallback(
    (raw: string) => {
      const cmd = raw.trim();
      if (!cmd) return;
      const res = runTerminalCommand(cmd, lang);
      setTermLines((prev) => {
        if (res.effect === 'clear') return [];
        return [
          ...prev,
          { text: `> ${cmd}`, tone: 'accent' as const },
          ...res.lines,
        ].slice(-300);
      });
      if (res.effect === 'exit') setMode('palette');
      const action = res.action;
      if (action) {
        window.setTimeout(() => {
          executeAction(action, router, lang);
          setOpen(false);
        }, 350);
      }
    },
    [router, setOpen, lang]
  );

  // ── Quick commands ────────────────────────────────────────────────────
  const quicks = useMemo<QuickCommand[]>(() => {
    const go = (action: UIAction) => () => {
      executeAction(action, router, lang);
      setOpen(false); // navigation or the comparison overlay is the payoff
    };
    const q = dict.ask.quick;
    const h = dict.ask.hint;
    return [
      { id: 'nav-research', label: q.research, hint: h.research, perform: go({ type: 'NAVIGATE', target: '/research' }) },
      { id: 'nav-projects', label: q.projects, hint: h.projects, perform: go({ type: 'NAVIGATE', target: '/projects' }) },
      { id: 'nav-agents', label: q.agents, hint: h.agents, perform: go({ type: 'NAVIGATE', target: '/agents' }) },
      { id: 'nav-knowledge', label: q.knowledge, hint: h.knowledge, perform: go({ type: 'NAVIGATE', target: '/knowledge' }) },
      { id: 'nav-observatory', label: q.observatory, hint: h.observatory, perform: go({ type: 'NAVIGATE', target: '/observatory' }) },
      { id: 'nav-publications', label: q.publications, hint: h.publications, perform: go({ type: 'NAVIGATE', target: '/publications' }) },
      {
        id: 'compare',
        label: q.compare,
        hint: h.compare,
        perform: go({ type: 'COMPARE', a: 'studyninja', b: 'knowledgebase-ai' }),
      },
      {
        id: 'ask-researching',
        // The label doubles as the question sent to the gateway, so the ask is
        // asked in the visitor's language.
        label: q.researching,
        hint: h.askLab,
        perform: () => submitAsk(q.researching),
      },
      {
        id: 'graph',
        label: q.graph,
        hint: h.graph,
        perform: go({ type: 'SHOW_GRAPH' }),
      },
      {
        id: 'terminal',
        label: q.terminal,
        hint: h.mode,
        perform: () => setMode('terminal'),
      },
    ];
  }, [router, setOpen, submitAsk, lang, dict]);

  const filtered = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return quicks;
    if (q.startsWith('>')) return [];
    return quicks.filter((c) => c.label.toLowerCase().includes(q));
  }, [input, quicks]);

  const listVisible =
    mode === 'palette' &&
    filtered.length > 0 &&
    (exchanges.length === 0 || input.trim().length > 0);

  // Screen-reader announcement. The visible transcript is NOT a live region:
  // its answer mutates token-by-token while streaming, which would announce
  // endless partial fragments. Instead this single node carries one stable
  // message per state — "thinking …" while pending/streaming (the string does
  // not change per token, so it is spoken once), then the completed answer or
  // error exactly once when the latest exchange settles.
  const liveMessage = useMemo(() => {
    const latest = exchanges[exchanges.length - 1];
    if (!latest) return '';
    if (latest.status === 'pending' || latest.status === 'streaming') {
      return dict.ask.thinking;
    }
    if (latest.status === 'error') return latest.error ?? '';
    return latest.answer.trim();
  }, [exchanges, dict]);

  // ── Submit ────────────────────────────────────────────────────────────
  const submit = useCallback(() => {
    const trimmed = input.trim();
    if (mode === 'terminal') {
      runTerm(trimmed);
      setInput('');
      return;
    }
    if (sel >= 0 && filtered[sel]) {
      filtered[sel].perform();
      setInput('');
      setSel(-1);
      return;
    }
    if (!trimmed) return;
    if (trimmed.toLowerCase() === 'terminal') {
      setMode('terminal');
      setInput('');
      return;
    }
    if (trimmed.startsWith('>')) {
      setMode('terminal');
      const rest = trimmed.slice(1).trim();
      if (rest) runTerm(rest);
      setInput('');
      return;
    }
    submitAsk(trimmed);
    setInput('');
  }, [input, mode, sel, filtered, runTerm, submitAsk]);

  const onInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (listVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSel((s) => Math.min(s + 1, filtered.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSel((s) => Math.max(s - 1, -1));
        return;
      }
    }
  };

  // ── Focus trap + Escape inside the dialog ─────────────────────────────
  const onDialogKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      // A first Escape interrupts an in-flight answer; a second closes.
      if (busy) {
        cancel();
        inputRef.current?.focus();
      } else {
        close();
      }
      return;
    }
    if (e.key === 'Tab') {
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <>
      {open && (
        <div
          className={`${styles.backdrop} ${reducedMotion ? styles.noMotion : ''}`}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={c.dialogAria}
            className={styles.panel}
            onKeyDown={onDialogKeyDown}
          >
            <header className={styles.head}>
              <span className="machine machine--sm">{dict.ask.interface}</span>
              <div className={styles.headRight}>
                <span className={`machine machine--sm ${styles.modeTag}`}>
                  {mode === 'terminal' ? dict.ask.terminal : dict.ask.palette}
                </span>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={close}
                  aria-label={c.closeAria}
                >
                  {dict.ask.esc}
                </button>
              </div>
            </header>

            <div ref={bodyRef} className={styles.body}>
              {mode === 'palette' && exchanges.length > 0 && (
                <div className={styles.transcript}>
                  {exchanges.map((ex) => (
                    <article key={ex.id} className={styles.exchange}>
                      <div className={`machine machine--sm ${styles.q}`}>
                        › {ex.question}
                      </div>
                      {ex.status === 'pending' && (
                        <div
                          className={`machine ${styles.thinking} ${
                            reducedMotion ? '' : 'pulse'
                          }`}
                        >
                          {dict.ask.thinking}
                        </div>
                      )}
                      {ex.answer && (
                        <div
                          className={styles.answer}
                          aria-busy={ex.status === 'streaming'}
                        >
                          {ex.answer.trimEnd()}
                        </div>
                      )}
                      {ex.error && (
                        <div className={`machine machine--sm ${styles.err}`}>
                          {ex.error}
                        </div>
                      )}
                      {ex.actions.length > 0 && (
                        <div className={styles.chips}>
                          {ex.actions.map((a, i) => (
                            <span key={i} className={styles.chip}>
                              {describeAction(a)}
                            </span>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}

              {listVisible && (
                <ul
                  className={styles.list}
                  role="listbox"
                  id="asklab-options"
                  aria-label={c.quickAria}
                >
                  {filtered.map((q, i) => (
                    <li
                      key={q.id}
                      id={`asklab-opt-${q.id}`}
                      role="option"
                      aria-selected={sel === i}
                      className={`${styles.opt} ${sel === i ? styles.optActive : ''}`}
                      onMouseEnter={() => setSel(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        q.perform();
                        setInput('');
                        setSel(-1);
                      }}
                    >
                      <span className={styles.optLabel}>{q.label}</span>
                      <span className={`machine machine--sm ${styles.optHint}`}>
                        {q.hint}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {mode === 'terminal' && (
                <div className={styles.term} role="log" aria-live="polite">
                  {termLines.map((l, i) => (
                    <div
                      key={i}
                      className={`${styles.termLine} ${
                        l.tone && l.tone !== 'default'
                          ? styles[TONE_CLASS[l.tone]]
                          : ''
                      }`}
                    >
                      {l.text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Single announcement point — see liveMessage above. */}
            <div
              className="visually-hidden"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {liveMessage}
            </div>

            <form
              className={styles.inputRow}
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <span className={styles.prompt} aria-hidden="true">
                {mode === 'terminal' ? '>_' : '›'}
              </span>
              <input
                ref={inputRef}
                type="text"
                className={`${styles.input} ${mode === 'terminal' ? styles.inputTerm : ''}`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onInputKeyDown}
                role="combobox"
                aria-expanded={listVisible}
                aria-controls="asklab-options"
                aria-autocomplete="list"
                aria-activedescendant={
                  listVisible && sel >= 0 && filtered[sel]
                    ? `asklab-opt-${filtered[sel].id}`
                    : undefined
                }
                aria-label={mode === 'terminal' ? c.termInputAria : c.askInputAria}
                placeholder={
                  mode === 'terminal' ? c.termPlaceholder : dict.ask.placeholder
                }
                autoComplete="off"
                spellCheck={false}
              />
              {busy && mode === 'palette' && (
                <button
                  type="button"
                  className={`machine machine--sm ${styles.stopBtn}`}
                  onClick={() => {
                    cancel();
                    inputRef.current?.focus();
                  }}
                  aria-label={c.stopAria}
                >
                  <span className={styles.stopDot} aria-hidden="true" />
                  {dict.ask.stop}
                </button>
              )}
            </form>

            <footer className={styles.foot}>
              <span className={`machine machine--sm ${styles.note}`}>
                {dict.ask.footnotePrefix}
                <a href={localePath(lang, '/privacy')}>{dict.ask.footnoteLink}</a>.
              </span>
              <span className={`machine machine--sm ${styles.keys}`}>
                ↑↓ {dict.ask.select} · ↵ {dict.ask.run} · {dict.ask.escClose}
              </span>
            </footer>
          </div>
        </div>
      )}
      <CompareView />
    </>
  );
}
